import React, { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import { ErrorOutline, CheckCircleOutline } from "@mui/icons-material";
import { updatePatientCareGap } from "apiHandler/actions";

import {
  getSourceValueForApi,
  mapCareGapItem,
  normalizeSourceType,
  parsePayersFromSource,
} from "./careGapUtils";
import SectionHeader from "./components/sectionHeader";
import BulkActionsBar from "./components/bulkActionsBar";
import OpenGapCard from "./components/openGapCard";
import ClosedGapCard from "./components/closedGapCard";
import GapCardSkeleton from "./components/gapCardSkeleton";
import EditCareGapDialog from "./components/editCareGapDialog";

const CareGapsPanels = ({ data = [], token, fetchData, loading }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    status: "",
    sourceType: "AI",
    payers: [],
    notes: "",
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSourceType, setBulkSourceType] = useState("PAYER");
  const [bulkPayers, setBulkPayers] = useState([]);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [editedNotesMap, setEditedNotesMap] = useState({});
  const [bulkSourceDirty, setBulkSourceDirty] = useState(false);

  const openGaps = useMemo(
    () =>
      (data || []).filter(
        (item) => item.status === "New" || item.status === "Active",
      ),
    [data],
  );

  const closedGaps = useMemo(
    () =>
      (data || []).filter(
        (item) => item.status === "Closed" || item.status === "Rejected",
      ),
    [data],
  );

  const mappedOpenGaps = useMemo(
    () => openGaps.map(mapCareGapItem),
    [openGaps],
  );
  const mappedClosedGaps = useMemo(
    () => closedGaps.map(mapCareGapItem),
    [closedGaps],
  );

  const selectedOpenItems = mappedOpenGaps.filter((item) =>
    selectedIds.includes(item.id),
  );

  const resetBulkUi = () => {
    setSelectedIds([]);
    setBulkEditMode(false);
    setEditedNotesMap({});
    setBulkPayers([]);
    setBulkSourceType("PAYER");
    setBulkSourceDirty(false);
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.length === mappedOpenGaps.length) {
      setSelectedIds([]);
      setBulkEditMode(false);
      return;
    }

    setSelectedIds(mappedOpenGaps.map((item) => item.id));
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleEdit = (item) => {
    const sourceType = normalizeSourceType(item.source);
    const payers = parsePayersFromSource(item.source);

    setSelectedItem(item);
    setForm({
      status: item.status,
      sourceType,
      payers,
      notes: item.notes || "",
    });
    setOpenDialog(true);
  };

  const handleSubmitSingle = async () => {
    if (!selectedItem) return;

    const sourceValue = getSourceValueForApi(form.sourceType, form.payers);

    const payload = {
      practice_id: selectedItem.practice_id,
      patient_id: selectedItem.patient_id,
      id: selectedItem.id,
      source: sourceValue,
      status: form.status,
      user_name: selectedItem.user_name || "Sri Ramesh Eevani",
      notes: form.notes || "",
    };

    try {
      setSubmitting(true);
      const res = await updatePatientCareGap(payload, token);
      if (res?.status === "Success") {
        fetchData?.();
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setSubmitting(false);
      setOpenDialog(false);
    }
  };

  const handleBulkUpdateSource = async () => {
    if (!selectedOpenItems.length) return;

    const sourceValue = getSourceValueForApi(bulkSourceType, bulkPayers);

    try {
      setSubmitting(true);

      await Promise.all(
        selectedOpenItems.map((item) =>
          updatePatientCareGap(
            {
              practice_id: item.practice_id,
              patient_id: item.patient_id,
              id: item.id,
              source: sourceValue,
              status: item.status,
              user_name: item.user_name || "Sri Ramesh Eevani",
              notes: editedNotesMap[item.id] ?? item.notes ?? "",
            },
            token,
          ),
        ),
      );

      setBulkSourceDirty(false);
      fetchData?.();
    } catch (error) {
      console.log("bulk update source error: ", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (!selectedOpenItems.length) return;

    try {
      setSubmitting(true);

      await Promise.all(
        selectedOpenItems.map((item) =>
          updatePatientCareGap(
            {
              practice_id: item.practice_id,
              patient_id: item.patient_id,
              id: item.id,
              source:
                bulkSourceDirty && selectedIds.includes(item.id)
                  ? getSourceValueForApi(bulkSourceType, bulkPayers)
                  : item.source,
              status,
              user_name: item.user_name || "Sri Ramesh Eevani",
              notes: editedNotesMap[item.id] ?? item.notes ?? "",
            },
            token,
          ),
        ),
      );

      fetchData?.();
      resetBulkUi();
    } catch (error) {
      console.log(`${status} selected error: `, error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnterBulkEdit = () => {
    const initialMap = {};
    selectedOpenItems.forEach((item) => {
      initialMap[item.id] = item.notes || item.text || "";
    });
    setEditedNotesMap(initialMap);
    setBulkEditMode(true);
  };

  const handleCancelBulkEdit = () => {
    setBulkEditMode(false);
    setEditedNotesMap({});
  };

  const handleBulkNoteChange = (id, value) => {
    setEditedNotesMap((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSaveBulkNotes = async () => {
    if (!selectedOpenItems.length) return;

    try {
      setSubmitting(true);

      await Promise.all(
        selectedOpenItems.map((item) =>
          updatePatientCareGap(
            {
              practice_id: item.practice_id,
              patient_id: item.patient_id,
              id: item.id,
              source:
                bulkSourceDirty && selectedIds.includes(item.id)
                  ? getSourceValueForApi(bulkSourceType, bulkPayers)
                  : item.source,
              status: item.status,
              user_name: item.user_name || "Sri Ramesh Eevani",
              notes: editedNotesMap[item.id] ?? item.notes ?? "",
            },
            token,
          ),
        ),
      );

      fetchData?.();
      setBulkEditMode(false);
      setEditedNotesMap({});
    } catch (error) {
      console.log("save bulk notes error: ", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} mt={2}>
        <Box
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 3,
            background: "#fcfcfd",
            border: "1px solid #e5e7eb",
          }}
        >
          <SectionHeader
            icon={<ErrorOutline />}
            title="Open Care Gaps"
            color="#db2777"
          />

          <BulkActionsBar
            openGaps={mappedOpenGaps}
            selectedIds={selectedIds}
            bulkSourceType={bulkSourceType}
            setBulkSourceType={(value) => {
              setBulkSourceType(value);
              setBulkSourceDirty(true);
            }}
            bulkPayers={bulkSourceType === "PAYER" ? bulkPayers : []}
            setBulkPayers={(updater) => {
              setBulkPayers(updater);
              setBulkSourceDirty(true);
            }}
            bulkEditMode={bulkEditMode}
            bulkSourceDirty={bulkSourceDirty}
            submitting={submitting}
            onToggleSelectAll={handleToggleSelectAll}
            onBulkUpdateSource={handleBulkUpdateSource}
            onCloseSelected={() => handleBulkStatusUpdate("Closed")}
            onRejectSelected={() => handleBulkStatusUpdate("Rejected")}
            onEditText={handleEnterBulkEdit}
            onSaveNotes={handleSaveBulkNotes}
            onCancelEdit={handleCancelBulkEdit}
          />

          <Stack spacing={1}>
            {loading
              ? [...Array(3)].map((_, i) => <GapCardSkeleton key={i} />)
              : mappedOpenGaps.map((item) => (
                  <OpenGapCard
                    key={item.id}
                    item={item}
                    checked={selectedIds.includes(item.id)}
                    bulkEditMode={bulkEditMode && selectedIds.includes(item.id)}
                    editedNote={
                      editedNotesMap[item.id] ?? item.notes ?? item.text
                    }
                    onToggle={handleToggleOne}
                    onNoteChange={handleBulkNoteChange}
                    onEditSingle={handleEdit}
                  />
                ))}
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 3,
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
          }}
        >
          <SectionHeader
            icon={<CheckCircleOutline />}
            title="Closed Care Gaps"
            color="#16a34a"
          />

          <Stack spacing={1}>
            {loading
              ? [...Array(3)].map((_, i) => <GapCardSkeleton key={i} />)
              : mappedClosedGaps.map((item) => (
                  <ClosedGapCard
                    key={item.id}
                    item={item}
                    onEdit={handleEdit}
                  />
                ))}
          </Stack>
        </Box>
      </Stack>

      <EditCareGapDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        form={form}
        setForm={setForm}
        submitting={submitting}
        onSave={handleSubmitSingle}
      />
    </>
  );
};

export default CareGapsPanels;
